import {
  ChatUiEventTypes,
  listenToParent,
  onUnauthorized,
  requestConfig,
  requestToken,
  sendChatUiEvent,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import {
  createAuthProvider,
  type AuthProvider,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-ts';
import {
  ChatProvider,
  ensureChatUiLocaleLoaded,
} from '@trimble-agentic-internal-npm-local/agentic-chat-ui';
import { useEffect, useMemo, useState } from 'react';

import { EmbeddingPageFactory } from './components/EmbeddingPageFactory';
import { ErrorComponent } from './components/Error';
import { SignInComponent } from './components/SignIn';
import { useTheme } from './hooks/useTheme';
import i18next from './i18n';
import {
  EmbeddedConfigProvider,
  type ExtendedChatUiConfiguration,
} from './providers/embedded-config-provider';

export const EmbeddedApp = () => {
  const { updateTheme } = useTheme();
  const [hasError, setHasError] = useState<boolean>(false);
  const [chatConfig, setChatConfig] = useState<
    ExtendedChatUiConfiguration | undefined
  >();

  useEffect(() => {
    if (!chatConfig) {
      requestConfig();
      return;
    }

    if (!chatConfig.uiConfig.showSignIn) {
      requestToken();
    }
  }, [chatConfig]);

  useEffect(() => {
    const unsubscribe = listenToParent(
      data => {
        setChatConfig({
          ...data.configuration,
        });
        updateTheme(data.configuration.uiConfig.theme);
        setHasError(false);
        void ensureChatUiLocaleLoaded(
          i18next,
          data.configuration.localization.locale,
          data.configuration.localization.selectedLanguage,
          undefined,
          data.configuration.localization.translations
        );
      },
      error => {
        setHasError(true);
        console.error(error);
      }
    );
    return () => unsubscribe();
  }, [updateTheme, setChatConfig]);

  const authProvider: AuthProvider = useMemo(
    () => createAuthProvider(requestToken, onUnauthorized),
    []
  );

  if (hasError) {
    return <ErrorComponent />;
  }

  if (!chatConfig) {
    return null;
  }

  if (chatConfig.uiConfig.showSignIn) {
    return (
      <SignInComponent
        onButtonClick={() => {
          sendChatUiEvent({
            type: ChatUiEventTypes.OnSignIn,
            payload: undefined,
          });
        }}
      />
    );
  }

  return (
    <ChatProvider
      environment={chatConfig.environment}
      authProvider={authProvider}
      features={chatConfig.features}
    >
      <EmbeddedConfigProvider chatConfig={chatConfig}>
        <EmbeddingPageFactory />
      </EmbeddedConfigProvider>
    </ChatProvider>
  );
};
